import { DateTime, type DurationUnits } from 'luxon';
import type { ICourseStatus, ICoursePathProgress, IUserCourseData } from '@klv/molecules/unified-search-results/types';

/**
 * Converts an ISO 8601 UTC string to a formatted local date/time string in the user's time zone.
 *
 * This function takes a date/time string in ISO 8601 format (which is assumed to be in UTC), converts it to the local
 * time zone, and returns a string formatted as "MMM dd, yyyy HH:mm ZZ" (e.g., "Mar 11, 2025 18:00 ET").
 * The formatting automatically adjusts to account for daylight saving time (DST) or standard time (ST) based on the
 * local time zone.
 *
 * It uses the `luxon` library to handle time zone conversion and formatting.
 *
 * @param iso - A string representing a date and time in ISO 8601 format, which includes a UTC time zone offset
 *              (e.g., "2025-03-12T00:00:00+00:00").
 * @param locale - (Optional) A string representing the locale code (e.g., "en-US", "fr-FR").
 *
 * @returns A string representing the local formatted date and time in the typical format "MMM dd, yyyy HH:mm ZZ",
 *          where "MMM" is the abbreviated month name, "dd" is the day of the month, "yyyy" is the year,
 *          "HH:mm" is the time in 24-hour format, and "ZZ" is the local time zone abbreviation (e.g., "ET").
 *          If the provided ISO string is invalid or cannot be parsed, the function returns an empty string.
 */
export const convertIsoUtcToFormattedLocal = (iso: string, locale?: string) => {
  const zoneName = DateTime.local().zoneName || 'America/New_York';
  const localDateTime = DateTime.fromISO(iso, { zone: zoneName, locale });

  if (!localDateTime.isValid) {
    return '';
  }

  let formattedDateTime = '';

  formattedDateTime = localDateTime.toLocaleString({
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    timeZoneName: 'short',
    timeZone: zoneName,
  });

  formattedDateTime = formattedDateTime.replace(/(\d{4}),/, '$1');

  const longZoneOffset = localDateTime.offsetNameLong;
  const isInDaylightSavingTime = longZoneOffset.match(/Daylight Time$/);
  const isInStandardTime = longZoneOffset.match(/Standard Time$/);

  if (isInDaylightSavingTime || isInStandardTime) {
    const daylightOrStandardRegExp = /(D|S)(?=T$)/;
    const isRegexValid = daylightOrStandardRegExp.test(formattedDateTime);

    if (isRegexValid) {
      formattedDateTime = formattedDateTime.replace(
        daylightOrStandardRegExp,
        '',
      );
    }
  }

  return formattedDateTime;
};

/**
 * Calculates the difference between the current date/time and a given ISO string date/time.
 *
 * @param {string} iso - An ISO 8601 formatted date/time string to compare against the current date/time.
 *
 * @returns {Object} An object containing:
 *   - nowDateTime: A DateTime object representing the current date/time in the local time zone.
 *   - endDateTime: A DateTime object representing the parsed ISO date/time in the local time zone.
 *   - diff: An object containing the time difference between the two dates with the following properties:
 *     - seconds: The difference in seconds.
 *     - minutes: The difference in minutes.
 *     - hours: The difference in hours.
 *     - days: The difference in days.
 */
export const getDateTimeDiffFromNow = (iso: string) => {
  const zoneName = DateTime.local().zoneName || 'America/New_York';
  const nowDateTime = DateTime.local({ zone: zoneName });
  const endDateTime = DateTime.fromISO(iso, { zone: zoneName });

  const fallback = {
    nowDateTime: '',
    endDateTime: '',
    diff: {
      seconds: 0,
      minutes: 0,
      hours: 0,
      days: 0,
    },
  };

  if (!nowDateTime.isValid) {
    return fallback;
  }
  if (!endDateTime.isValid) {
    return fallback;
  }

  const diffUnits: DurationUnits = ['days', 'hours', 'minutes', 'seconds'];
  const diff = endDateTime.diff(nowDateTime, diffUnits);

  if (!diff.isValid) {
    return fallback;
  }

  const { seconds = 0, minutes = 0, hours = 0 } = diff.toObject();
  let { days = 0 } = diff.toObject();

  // Update days to rely on calendar days instead of the diff object:
  // The diff object is too accurate, it relies on a full 24 hours.
  if (days) {
    const adjustedEndDateTime = endDateTime.set({
      hour: 23,
      minute: 59,
      second: 0,
      millisecond: 0,
    });
    const { days: newDays = 0 } = adjustedEndDateTime.diff(
      nowDateTime,
      diffUnits,
    );
    days = newDays;
  }

  return {
    nowDateTime: nowDateTime,
    endDateTime: endDateTime,
    diff: {
      seconds,
      minutes,
      hours,
      days,
    },
  };
};

/**
 * Helper function to calculate course progress percentage
 */
export const calculateCourseProgress = (course: {
  completed_lesson_count: number;
  total_lesson_count: number;
  completed_at?: string | null;
}): number => {
  // If course is marked as completed, assume quiz was passed - 100%
  if (course.completed_at && course.completed_at !== '') {
    return 100;
  }

  // Prevent division by zero
  if (course.total_lesson_count === 0) {
    return 0;
  }

  // Otherwise calculate based on completed/total lessons
  return Math.round(
    (course.completed_lesson_count / course.total_lesson_count) * 100,
  );
};

export const findCourseWithProgress = ({
  userCourseData,
  courseId,
}: {
  userCourseData?: IUserCourseData;
  courseId: string;
}): ICourseStatus | undefined => {
  if (!userCourseData || !courseId) return undefined;
  
  // First search individual courses
  if (Array.isArray(userCourseData.course_progress)) {
    const courseProgress = userCourseData.course_progress.find(
      (course: ICourseStatus) => course.third_party_id === courseId,
    );
    if (courseProgress) return courseProgress;
  }
  
  // Then search course paths and convert to course format
  if (Array.isArray(userCourseData.course_path_progress)) {
    const pathProgress = userCourseData.course_path_progress.find(
      (path: ICoursePathProgress) => path.third_party_id === courseId,
    );
    if (pathProgress) {
      // Convert course path progress to course status format
      return {
        third_party_id: pathProgress.third_party_id,
        completed_lesson_count: pathProgress.completed_course_count,
        total_lesson_count: pathProgress.total_course_count,
        completed_at: pathProgress.completed_at
      } as ICourseStatus;
    }
  }
  
  return undefined;
};

export const isViewedResource = ({
  courseId,
  viewedResources,
}: {
  courseId: string;
  viewedResources: string[];
}) => viewedResources?.includes(courseId as string);
